import { createContext, useContext } from 'react'

const UserContext = createContext(null)

export const UserProvider = ({ user, children }) => {
  // Helper function to check if user has access to a specific operator
  const hasOperatorAccess = (operatorCode) => {
    if (!user) {
      console.log('[UserContext] hasOperatorAccess: no user')
      return false
    }
    
    // ADMIN has access to everything
    if (user.role === 'ADMIN') {
      console.log('[UserContext] hasOperatorAccess:', operatorCode, '-> true (ADMIN)')
      return true
    }
    
    // If no restrictions (null or empty array), user has access to all
    if (!user.allowed_operators || user.allowed_operators.length === 0) {
      console.log('[UserContext] hasOperatorAccess:', operatorCode, '-> true (no restrictions)')
      return true
    }
    
    // Check if operator is in allowed list
    const hasAccess = user.allowed_operators.includes(operatorCode)
    console.log('[UserContext] hasOperatorAccess:', operatorCode, 'in', user.allowed_operators, '->', hasAccess)
    return hasAccess
  }

  // Get list of accessible operators
  const getAccessibleOperators = (operators) => {
    if (!user) return []
    
    // ADMIN sees all
    if (user.role === 'ADMIN') return operators
    
    // No restrictions - see all
    if (!user.allowed_operators || user.allowed_operators.length === 0) {
      return operators
    }
    
    // Filter by allowed operators
    return operators.filter(op => user.allowed_operators.includes(op))
  }

  // Helper function to check if user has access to a specific feature
  const hasFeatureAccess = (featureCode) => {
    if (!user) return false
    
    // ADMIN has access to everything
    if (user.role === 'ADMIN') return true
    
    // If no restrictions, check default role-based access
    if (!user.allowed_features || user.allowed_features.length === 0) {
      // Default feature access by role (matches backend logic)
      const roleDefaults = {
        'ADMIN': ['AI_ANALYSIS', 'EXPORT_DATA', 'ADVANCED_FILTERS', 'BULK_OPERATIONS'],
        'MANAGER': ['AI_ANALYSIS', 'EXPORT_DATA', 'ADVANCED_FILTERS'],
        'KAM': ['EXPORT_DATA'],
        'VIEWER': [],
        'OPERATOR_EXTERNAL': []
      }
      const defaultFeatures = roleDefaults[user.role] || []
      return defaultFeatures.includes(featureCode)
    }
    
    // Check if feature is in allowed list
    return user.allowed_features.includes(featureCode)
  }

  const value = {
    user,
    hasOperatorAccess,
    getAccessibleOperators,
    hasFeatureAccess,
    isAdmin: user?.role === 'ADMIN',
    allowedOperators: user?.allowed_operators || null,
    allowedFeatures: user?.allowed_features || null
  }

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  )
}

export const useUser = () => {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUser must be used within UserProvider')
  }
  return context
}

